import { useEffect, useRef } from "react";
import * as signalR from "@microsoft/signalr";
import axios from "axios";
import logger from "../utils/logger";
import { Eventbus } from "../utils/index";
import { events } from "../utils/constants/index";
import ToastService from "./toast.service";
import { AuthenticationService } from "./authentication.service";
import config from "../config";

const signalRAxios = axios.create({
  baseURL: config.centreServiceApiUrl,
  headers: { 'content-type': 'application/json' },
  withCredentials: true
});

signalRAxios.interceptors.request.use(async (request) => {
  let isAuthenticated = AuthenticationService.isAuthenticated();
  if (!isAuthenticated) {
    await AuthenticationService.refreshToken();
    isAuthenticated = AuthenticationService.isAuthenticated();
  }
  if (!isAuthenticated) {
    return Promise.reject(new Error('User not authenticated'));
  }
  request.withCredentials = true;
  return request;
}, (error) => Promise.reject(error));

async function getSignalRConnectionDirect(abortSignal) {
  try {
    const response = await signalRAxios.get('/api/SignalR/negotiate', {
      signal: abortSignal
    });
    return response.data?.data || response.data;
  } catch (error) {
    logger.error('Error fetching SignalR connection:', error);
    throw error;
  }
}

const processDataChangeMessage = (eventName, message) => {
  logger.debug(`Processing SignalR message for ${eventName}:`, message);
  try {
    if (eventName.includes(".delete")) {
      let id = message;
      if (typeof message === "string") {
        try {
          id = JSON.parse(message);
        } catch (e) {
          logger.debug(`Delete message is not JSON, using as string:`, message);
        }
      }
      Eventbus.emit(eventName, id);
      return;
    }

    if (typeof message === "string") {
      try {
        const parsed = JSON.parse(message);
        Eventbus.emit(eventName, parsed);
      } catch (e) {
        logger.warn(`Failed to parse SignalR message for ${eventName}:`, message);
      }
    } else {
      Eventbus.emit(eventName, message);
    }
  } catch (error) {
    logger.error(`Error processing SignalR ${eventName}:`, error, message);
  }
};

export async function connectToSignalR({ connection }) {
  logger.log("Connecting to SignalR...");
  try {
    if (!connection) {
      throw new Error("No connection object provided");
    }

    const hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(connection.url, {
        accessTokenFactory: () => {
          logger.debug("SignalR using connection token");
          return connection.accessToken;
        },
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    const META_EVENTS = new Set([events.SIGNALR.CONNECTED, events.SIGNALR.DISCONNECTED]);

    const registerAllHandlers = () => {
      logger.log("Re-registering all SignalR event handlers");
      Object.entries(events.SIGNALR)
        .filter(([, eventName]) => !META_EVENTS.has(eventName))
        .forEach(([eventKey, eventName]) => {
        logger.debug(`Registering SignalR handler for: ${eventName}`);
        try {
          if (typeof hubConnection.off === 'function') {
            hubConnection.off(eventName);
          }
        } catch (e) {
          logger.debug(`Error calling off() for ${eventName}:`, e);
        }
        hubConnection.on(eventName, (message) => processDataChangeMessage(eventName, message));
      });
    };

    registerAllHandlers();

    await hubConnection.start();
    logger.log("SignalR connection established");

    Eventbus.emit(events.SIGNALR.CONNECTED);

    hubConnection.onclose(async (error) => {
      logger.log("SignalR connection closed", error);
      Eventbus.emit(events.SIGNALR.DISCONNECTED);
      connectionManager.connection = null;

      setTimeout(() => {
        logger.log("Attempting to reconnect SignalR after connection close");
        connectionManager.getConnection().catch((err) => {
          logger.error("Failed to reconnect SignalR:", err);
        });
      }, 2000);
    });

    hubConnection.onreconnected(() => {
      logger.log("SignalR reconnected");
      registerAllHandlers();
      Eventbus.emit(events.SIGNALR.CONNECTED);
    });

    hubConnection.onreconnecting(() => {
      logger.log("SignalR reconnecting");
      Eventbus.emit(events.SIGNALR.DISCONNECTED);
    });

    return hubConnection;
  } catch (error) {
    logger.error("Error connecting to SignalR:", error);
    throw error;
  }
}

const connectionManager = {
  connection: null,
  connectingPromise: null,
  lastAttempt: 0,
  attemptCount: 0,
  lastHealthCheck: 0,
  healthCheckInterval: 30000,
  healthCheckTimerId: null,
  lastReconnectAttempt: 0,
  minReconnectInterval: 5000,
  minRetryInterval: 1000,
  maxAttempts: 5,

  async verifyConnectionHealth() {
    const now = Date.now();
    if (now - this.lastHealthCheck < this.healthCheckInterval) {
      return this.connection && this.connection.state === signalR.HubConnectionState.Connected;
    }

    this.lastHealthCheck = now;

    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      logger.warn("Connection health check failed: not connected");

      if (now - this.lastReconnectAttempt < this.minReconnectInterval) {
        logger.debug("Reconnect attempt throttled, waiting before retry");
        return false;
      }

      this.lastReconnectAttempt = now;
      logger.warn("Attempting reconnection due to failed health check");
      this.connection = null;
      await this.getConnection().catch((error) => {
        logger.error("Reconnection attempt failed during health check:", error);
      });
      return false;
    }

    try {
      logger.debug("SignalR connection health check passed");
      return true;
    } catch (error) {
      logger.error("SignalR connection health check failed:", error);
      return false;
    }
  },

  startPeriodicHealthCheck() {
    if (this.healthCheckTimerId) {
      logger.debug("Health check already running");
      return;
    }

    logger.log("Starting periodic SignalR health checks every", this.healthCheckInterval, "ms");
    this.healthCheckTimerId = setInterval(() => {
      this.verifyConnectionHealth().catch((error) => {
        logger.error("Error during periodic health check:", error);
      });
    }, this.healthCheckInterval);
  },

  stopPeriodicHealthCheck() {
    if (this.healthCheckTimerId) {
      logger.log("Stopping periodic SignalR health checks");
      clearInterval(this.healthCheckTimerId);
      this.healthCheckTimerId = null;
    }
  },

  async getConnection() {
    if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
      const isHealthy = await this.verifyConnectionHealth();
      if (isHealthy) {
        return this.connection;
      } else {
        this.connection = null;
      }
    }

    if (this.connectingPromise) {
      return await this.connectingPromise;
    }

    const now = Date.now();
    if (now - this.lastAttempt < this.minRetryInterval) {
      await new Promise((resolve) => setTimeout(resolve, this.minRetryInterval - (now - this.lastAttempt)));
    }

    return await this.createConnection();
  },

  async createConnection() {
    if (this.connectingPromise) {
      return await this.connectingPromise;
    }

    this.lastAttempt = Date.now();
    this.attemptCount = 0;

    const doConnect = async () => {
      try {
        if (this.connection) {
          await this.connection.stop().catch((err) => logger.error("Error stopping existing connection:", err));
          this.connection = null;
        }

        let lastError;
        while (this.attemptCount < this.maxAttempts) {
          this.attemptCount += 1;
          const abortController = new AbortController();

          try {
            logger.log("Fetching SignalR connection details... attempt", this.attemptCount);

            const timeoutId = setTimeout(() => {
              abortController.abort();
            }, 15000);

            const response = await getSignalRConnectionDirect(abortController.signal);
            clearTimeout(timeoutId);

            if (!response?.url || !response?.accessToken) {
              throw new Error("Invalid SignalR connection response");
            }

            this.connection = await connectToSignalR({ connection: response });
            return this.connection;
          } catch (error) {
            lastError = error;
            logger.error(`SignalR connection attempt ${this.attemptCount} failed:`, error);

            if (this.attemptCount >= this.maxAttempts) break;

            const waitMs = Math.min(2000 * Math.pow(2, this.attemptCount - 1), 30000);
            const jitter = Math.floor(Math.random() * 200);
            await new Promise((resolve) => setTimeout(resolve, waitMs + jitter));
          }
        }

        logger.error("All SignalR connection attempts failed", lastError);
        ToastService.error("Error establishing SignalR connection", "signalRError");
        throw lastError;
      } finally {
        this.connectingPromise = null;
      }
    };

    this.connectingPromise = doConnect();
    return await this.connectingPromise;
  },

  async disconnect() {
    if (this.connection) {
      await this.connection.stop().catch((err) => logger.error("Error stopping SignalR connection:", err));
      this.connection = null;
    }
    this.stopPeriodicHealthCheck();
    this.connectingPromise = null;
    this.attemptCount = 0;
  },
};

export const getSignalRConnectionState = () => {
  return connectionManager.connection && connectionManager.connection.state === signalR.HubConnectionState.Connected;
};

export const ensureSignalRConnected = async () => {
  try {
    const connection = await connectionManager.getConnection();
    if (connection && connection.state === signalR.HubConnectionState.Connected) {
      return true;
    }
  } catch (error) {
    logger.error("Failed to verify SignalR connection:", error);
  }
  return false;
};

export { processDataChangeMessage, connectionManager };

export default function SignalRService() {
  const initializationRef = useRef(false);

  useEffect(() => {
    if (initializationRef.current) return;

    initializationRef.current = true;
    logger.log("Initializing SignalR service...");

    connectionManager.getConnection()
      .then(() => {
        logger.log("Initial SignalR connection established, starting periodic health checks");
        connectionManager.startPeriodicHealthCheck();
      })
      .catch((error) => {
        logger.error("Failed to initialize SignalR connection:", error);
        connectionManager.startPeriodicHealthCheck();
      });

    return () => {
      connectionManager.disconnect().catch((error) => {
        logger.error("Error during SignalR service cleanup:", error);
      });
    };
  }, []);

  return null;
}
